'use client';

import { useRef } from 'react';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { cn } from '@packages/shared/src/utils/cn/cn';

import { defineMessages, useIntl } from 'react-intl';
import { usePathname, useRouter } from 'next/navigation';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { defineStepper } from '@packages/shared/src/ui/Stepper/Stepper';
import type { CheckoutTargetPage } from '@packages/gql/generated/shopping/graphql';
import { useUpdatePreparedBasket } from '../../hooks/useUpdatePreparedBasket';
import { getPathForTargetPage, getTargetPageForPath } from '../../utils/checkoutPages';

const messages = defineMessages({
  basket: {
    id: 'ProgressBar.basket',
    defaultMessage: 'Warenkorb',
  },
  login: {
    id: 'ProgressBar.login',
    defaultMessage: 'Anmeldung',
  },
  delivery: {
    id: 'ProgressBar.delivery',
    defaultMessage: 'Lieferung',
  },
  payment: {
    id: 'ProgressBar.payment',
    defaultMessage: 'Zahlung',
  },
  checkorder: {
    id: 'ProgressBar.checkorder',
    defaultMessage: 'Prüfen & Bestellen',
  },
  bypassHeadline: {
    id: 'ProgressBar.Bypass.Headline',
    defaultMessage: 'Bestellnavigation überspringen',
  },
});

type ProgressBarStep = {
  id: string;
  title: string;
  icon: React.ReactNode;
  targetPage: CheckoutTargetPage;
  localizedPath: string;
};

type ProgressBarProps = {
  activeStep: number;
  /**
   * Saves the current page's state together with the navigation intent and
   * resolves with the backend-confirmed target page (null when saving failed).
   * When omitted, the ProgressBar sends an intent-only updatePreparedBasket
   * so the backend always decides (and caches) the resulting targetPage.
   */
  saveAndNavigate?: (
    targetPage: CheckoutTargetPage,
  ) => Promise<CheckoutTargetPage | null | undefined>;
};

export const ProgressBar = ({ activeStep, saveAndNavigate }: ProgressBarProps) => {
  const { formatMessage, locale } = useIntl();
  const router = useRouter();
  const pathname = usePathname();
  const {
    order: { paths },
  } = useConfig();
  const { loggedIn } = useLoginState();
  const [updateState, updatePreparedBasket] = useUpdatePreparedBasket();
  // Synchronous lock: `updateState.fetching` only flips on the next render, so
  // rapid repeated clicks could otherwise fire concurrent save mutations.
  const isNavigatingRef = useRef(false);

  const currentPage = getTargetPageForPath(pathname ?? '', paths);
  const activeIconClass = (targetPage: CheckoutTargetPage) =>
    cn('size-4', currentPage === targetPage && 'outline-color-primary outline-2 outline-offset-2');
  const isStepClickable = (targetPage: CheckoutTargetPage) =>
    !(targetPage === 'LOGIN_PAGE' && loggedIn);
  const activeTitleClass = (targetPage: CheckoutTargetPage) =>
    cn(
      currentPage === targetPage && 'text-color-primary font-bold',
      !isStepClickable(targetPage) && 'cursor-default',
    );

  const { Stepper } = defineStepper<ProgressBarStep[]>([
    {
      id: 'step-1',
      title: formatMessage(messages.basket),
      icon: <span className={cn('icon-emp-cart', activeIconClass('BASKET'))} />,
      targetPage: 'BASKET',
      localizedPath: getLocalizedPath(paths.basket, locale),
    },
    {
      id: 'step-2',
      title: formatMessage(messages.login),
      icon: loggedIn ? (
        <span className={cn('icon-emp-login-opened', activeIconClass('LOGIN_PAGE'))} />
      ) : (
        <span className={cn('icon-emp-login-closed', activeIconClass('LOGIN_PAGE'))} />
      ),
      targetPage: 'LOGIN_PAGE',
      localizedPath: getLocalizedPath(paths.login, locale),
    },
    {
      id: 'step-3',
      title: formatMessage(messages.delivery),
      icon: <span className={cn('icon-emp-delivery', activeIconClass('DELIVERY_PAGE'))} />,
      targetPage: 'DELIVERY_PAGE',
      localizedPath: getLocalizedPath(paths.delivery, locale),
    },
    {
      id: 'step-4',
      title: formatMessage(messages.payment),
      icon: <span className={cn('icon-emp-billing', activeIconClass('PAYMENT_PAGE'))} />,
      targetPage: 'PAYMENT_PAGE',
      localizedPath: getLocalizedPath(paths.payment, locale),
    },
    {
      id: 'step-5',
      title: formatMessage(messages.checkorder),
      icon: <span className={cn('icon-emp-check-order', activeIconClass('CHECK_AND_ORDER'))} />,
      targetPage: 'CHECK_AND_ORDER',
      localizedPath: getLocalizedPath(paths.checkandorder, locale),
    },
  ]);

  const saveNavigationIntent = async (targetPage: CheckoutTargetPage) => {
    try {
      const result = await updatePreparedBasket({
        locale,
        page: currentPage,
        changeOrderableBasketInput: { targetPage },
      });
      const response = result.data?.updatePreparedBasket;
      return response?.__typename === 'CheckoutOrderableBasket' ? response.targetPage : null;
    } catch {
      return null;
    }
  };

  const navigateWithSave = async (step: ProgressBarStep) => {
    if (
      isNavigatingRef.current ||
      updateState.fetching ||
      step.targetPage === currentPage ||
      !isStepClickable(step.targetPage)
    ) {
      return;
    }
    isNavigatingRef.current = true;

    try {
      // The basket page is live for all tenants while the rest of the checkout
      // may still be served by the legacy fragment flow (infra cookie routing).
      // Entering the checkout from the basket must therefore stay a plain
      // navigation and must not touch the GQL prepared basket.
      if (currentPage === 'BASKET') {
        router.push(step.localizedPath);
        return;
      }

      const confirmedPage = saveAndNavigate
        ? await saveAndNavigate(step.targetPage).catch(() => null)
        : await saveNavigationIntent(step.targetPage);

      // Navigate to the backend-confirmed page; fall back to the clicked step so
      // navigation still works when saving fails (route correction takes over).
      const targetPath = confirmedPage ? getPathForTargetPage(confirmedPage, paths) : undefined;
      router.push(targetPath ? getLocalizedPath(targetPath, locale) : step.localizedPath);
    } finally {
      isNavigatingRef.current = false;
    }
  };

  const onClickHandler = (step: ProgressBarStep) => {
    navigateWithSave(step).catch(() => undefined);
    return undefined;
  };

  return (
    <div className="w-full px-2 pt-2 pb-1.5 md:px-0 md:pb-[1.805rem]">
      <BypassBlock buttonText={formatMessage(messages.bypassHeadline)} skipScrollIntoView>
        <Stepper.Provider
          labelOrientation="vertical"
          defaultStep={`step-${activeStep}` as 'step-1'}
        >
          {({ stepper }) => (
            <Stepper.Navigation>
              {stepper.steps.map((step) => (
                <Stepper.Step
                  key={step.id}
                  of={step.id}
                  onClick={() => onClickHandler(step)}
                  icon={step.icon}
                >
                  <Stepper.Title
                    className={activeTitleClass(step.targetPage)}
                    onClick={() => onClickHandler(step)}
                  >
                    {step.title}
                  </Stepper.Title>
                </Stepper.Step>
              ))}
            </Stepper.Navigation>
          )}
        </Stepper.Provider>
      </BypassBlock>
    </div>
  );
};
